<template>
    <main class="news-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <span class="tbv2-current">{{ $t('news.page.title') }}</span>
            </div>
        </portal>

        <header class="news-hero">
            <div>
                <h1 class="news-hero-title">{{ $t('news.page.title') }}</h1>
                <p class="news-hero-sub">{{ $t('news.page.subtitle') }}</p>
            </div>

            <div class="news-hero-actions">
                <BmvButton
                    v-if="canManageCategories"
                    variant="secondary"
                    icon="fal fa-tags"
                    @click="goToCategories"
                >
                    {{ $t('news.page.manage_categories') }}
                </BmvButton>
                <BmvButton
                    v-if="canCreate"
                    variant="primary"
                    icon="fal fa-plus"
                    @click="goToCreate"
                >
                    {{ $t('news.page.new') }}
                </BmvButton>
            </div>
        </header>

        <NewsFilters
            :categories="categories || []"
            :value="filters"
            @change="onFiltersChange"
        />

        <BmvTable
            :columns="tableColumns"
            :data="articlesList"
            :loading="isLoading"
            :pagination="true"
            :total-results="totalResults"
            :total-pages="totalPages"
            :current-page="currentPage"
            :per-page="articlesConfig.per_page"
            :sort-field="articlesConfig.sort"
            :sort-direction="articlesConfig.sortDirection"
            :empty-text="$t('news.page.empty')"
            @sort="onSort"
            @page-change="onPageChange"
        >
            <template #cell-cover="{ row }">
                <div class="nl-cover">
                    <img
                        v-if="coverUrl(row)"
                        :src="coverUrl(row)"
                        :alt="articleTitle(row)"
                    />
                    <span v-else class="nl-cover-empty">
                        <i class="fal fa-image"></i>
                    </span>
                </div>
            </template>

            <template #cell-title="{ row }">
                <router-link :to="`/news/${row.id}`" class="bmv-cell-link">
                    {{ articleTitle(row) || '—' }}
                </router-link>
            </template>

            <template #cell-category="{ row }">
                <span v-if="row.category" class="nl-category">
                    {{ categoryLabel(row.category) }}
                </span>
                <span v-else class="nl-muted">—</span>
            </template>

            <template #cell-status="{ row }">
                <span
                    class="nl-badge"
                    :class="row.status === 'published' ? 'nl-badge--pub' : 'nl-badge--draft'"
                >
                    <i
                        :class="row.status === 'published' ? 'fal fa-check-circle' : 'fal fa-pen'"
                    ></i>
                    {{ $t(`news.status.${row.status || 'draft'}`) }}
                </span>
            </template>

            <template #cell-published_at="{ row }">
                <span v-if="row.published_at">
                    {{ formatDate(row.published_at) }}
                </span>
                <span v-else class="nl-muted">—</span>
            </template>

            <template #cell-author="{ row }">
                <span v-if="row.created_by">
                    {{ row.created_by.name || row.created_by.email || '—' }}
                </span>
                <span v-else class="nl-muted">—</span>
            </template>

            <template #cell-actions="{ row }">
                <div class="nl-actions">
                    <BmvButton
                        variant="ghost"
                        size="sm"
                        icon="fal fa-eye"
                        :title="$t('common.view')"
                        @click="viewArticle(row)"
                    />
                    <BmvButton
                        v-if="canUpdate"
                        variant="ghost"
                        size="sm"
                        icon="fal fa-pen"
                        :title="$t('common.edit')"
                        @click="editArticle(row)"
                    />
                    <BmvButton
                        v-if="canUpdate && row.status === 'draft'"
                        variant="success"
                        size="sm"
                        icon="fal fa-paper-plane"
                        :title="$t('news.actions.publish')"
                        :loading="busyId === row.id"
                        @click="togglePublish(row, true)"
                    />
                    <BmvButton
                        v-else-if="canUpdate && row.status === 'published'"
                        variant="secondary"
                        size="sm"
                        icon="fal fa-ban"
                        :title="$t('news.actions.unpublish')"
                        :loading="busyId === row.id"
                        @click="togglePublish(row, false)"
                    />
                    <BmvButton
                        v-if="canDelete"
                        variant="danger"
                        size="sm"
                        icon="fal fa-trash"
                        :title="$t('common.delete')"
                        @click="confirmDelete(row)"
                    />
                </div>
            </template>
        </BmvTable>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvTable from '@/components/UI/BmvTable.vue'
import NewsFilters from '@/components/News/NewsFilters.vue'

export default {
    name: 'NewsList',
    components: { BmvButton, BmvTable, NewsFilters },
    data() {
        return {
            isLoading: false,
            busyId: null,
            tableColumns: [
                { field: 'cover', label: this.$t('news.table.cover'), width: '80px' },
                { field: 'title', label: this.$t('news.table.title'), sortable: true, width: '32%' },
                { field: 'category', label: this.$t('news.table.category'), width: '14%' },
                { field: 'status', label: this.$t('news.table.status'), width: '12%' },
                { field: 'published_at', label: this.$t('news.table.published_at'), sortable: true, width: '12%' },
                { field: 'author', label: this.$t('news.table.author'), width: '14%' },
                { field: 'actions', label: this.$t('news.table.actions'), width: '160px', align: 'right' }
            ]
        }
    },
    computed: {
        ...mapState('news', {
            articles: (state) => state.articles,
            categories: (state) => state.categories,
            articlesConfig: (state) => state.articlesConfig
        }),
        lang() {
            return this.$store.state.lang
        },
        filters() {
            return this.articlesConfig.filters || {}
        },
        articlesList() {
            return (this.articles && this.articles.data) || []
        },
        totalResults() {
            return (this.articles && this.articles.total) || 0
        },
        currentPage() {
            return (this.articles && this.articles.current_page) || 1
        },
        totalPages() {
            if (!this.articles || !this.articles.total || !this.articles.per_page) {
                return 0
            }
            return Math.ceil(this.articles.total / this.articles.per_page)
        },
        permissions() {
            return JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canCreate() {
            return this.permissions.includes('news.create')
        },
        canUpdate() {
            return this.permissions.includes('news.update')
        },
        canDelete() {
            return this.permissions.includes('news.delete')
        },
        canManageCategories() {
            return this.permissions.includes('news-categories.view')
        }
    },
    created() {
        this.loadCategories()
        this.loadArticles()
    },
    methods: {
        ...mapActions('news', [
            'getArticles',
            'getCategories',
            'deleteArticle',
            'publishArticle',
            'unpublishArticle'
        ]),
        articleTitle(row) {
            if (!row) return ''
            const translations = row.translations || []
            const match =
                translations.find((t) => t.locale === this.lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && match.title) || row.title || ''
        },
        categoryLabel(cat) {
            if (!cat) return ''
            const translations = cat.translations || []
            const match =
                translations.find((t) => t.locale === this.lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && (match.name || match.title)) || cat.name || '—'
        },
        coverUrl(row) {
            const media = row && row.cover_media
            if (!media) return null
            return media.url || media.path || null
        },
        formatDate(iso) {
            if (!iso) return ''
            return this.moment(iso).locale(this.lang).format('L')
        },
        loadCategories() {
            return this.getCategories().catch(() => {})
        },
        loadArticles() {
            this.isLoading = true
            return this.getArticles()
                .catch((err) => this.$store.dispatch('dispatchError', err))
                .finally(() => {
                    this.isLoading = false
                })
        },
        onFiltersChange(filters) {
            this.articlesConfig.filters = { ...filters }
            this.articlesConfig.page = 1
            this.loadArticles()
        },
        onSort({ field, direction }) {
            this.articlesConfig.sort = field
            this.articlesConfig.sortDirection = direction
            this.articlesConfig.page = 1
            this.loadArticles()
        },
        onPageChange(page) {
            this.articlesConfig.page = page
            this.loadArticles()
        },
        goToCreate() {
            this.$router.push({ name: 'NewsCreate' })
        },
        goToCategories() {
            this.$router.push({ name: 'NewsCategories' })
        },
        viewArticle(row) {
            this.$router.push({ name: 'NewsView', params: { id: row.id } })
        },
        editArticle(row) {
            this.$router.push({ name: 'NewsEdit', params: { id: row.id } })
        },
        async togglePublish(row, shouldPublish) {
            if (this.busyId) return
            this.busyId = row.id
            try {
                if (shouldPublish) {
                    await this.publishArticle(row.id)
                } else {
                    await this.unpublishArticle(row.id)
                }
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t(
                        shouldPublish
                            ? 'news.actions.publish'
                            : 'news.actions.unpublish'
                    )
                })
                await this.loadArticles()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyId = null
            }
        },
        async confirmDelete(row) {
            const dialog = this.$bmvDialog
            const confirmed =
                dialog && typeof dialog.confirm === 'function'
                    ? await dialog.confirm({
                          variant: 'danger',
                          title: this.$t('news.actions.delete_confirm'),
                          confirmLabel: this.$t('common.delete'),
                          cancelLabel: this.$t('common.cancel')
                      })
                    : window.confirm(this.$t('news.actions.delete_confirm'))
            if (!confirmed) return
            try {
                await this.deleteArticle(row.id)
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t('common.delete')
                })
                await this.loadArticles()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            }
        }
    }
}
</script>

<style scoped>
.news-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.news-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: var(--v2-space-6);
    flex-wrap: wrap;
}

.news-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.news-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 820px;
}

.news-hero-actions {
    display: inline-flex;
    gap: 8px;
}

.nl-cover {
    width: 56px;
    height: 40px;
    border-radius: var(--v2-radius-sm);
    overflow: hidden;
    background: var(--v2-surface-2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v2-text-4);
}

.nl-cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.nl-cover-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.nl-cover-empty i {
    font-size: 1rem;
}

.nl-category {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
}

.nl-muted {
    color: var(--v2-text-4);
}

.nl-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.nl-badge i {
    font-size: 0.7rem;
}

.nl-badge--draft {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.nl-badge--pub {
    background: var(--v2-success-soft);
    color: var(--v2-success);
}

.nl-actions {
    display: inline-flex;
    gap: 6px;
    justify-content: flex-end;
}

@media (max-width: 900px) {
    .news-v2 {
        padding: 16px 18px 32px;
    }
}
</style>
